<template>
  <div
    class="input-quantity"
    :class="{
      'no-border': hideButtons,
      disabled: disabled,
      shadow,
      white,
    }"
  >
    <ButtonClassic
      v-if="!hideButtons"
      variant="special"
      color="primary"
      :disabled="disabled || modelValue === min"
      icon="left"
      @click="changeValue(parseInt(modelValue) - step, true)"
    >
      <template #left-icon>
        <UilMinus />
      </template>
    </ButtonClassic>
    <input
      class="input text-regular"
      :class="status"
      type="number"
      :disabled="disabled"
      :min="min"
      :max="max"
      :step="step"
      :value="modelValue"
      @input="changeValue(parseInt($event.target.value))"
      @blur="validateValue"
    />
    <ButtonClassic
      v-if="!hideButtons"
      variant="special"
      color="primary"
      :disabled="disabled || modelValue === max"
      icon="right"
      @click="changeValue(parseInt(modelValue) + step, true)"
    >
      <template #right-icon>
        <UilPlus />
      </template>
    </ButtonClassic>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ButtonClassic from "../../components/buttons/ButtonClassic.vue";
import { UilMinus, UilPlus } from "@lde/vue-unicons";

/**
 * Permet de sélectionner une quantité manuellement ou par incrément.
 */
export default defineComponent({
  name: "InputQuantity",
  components: {
    ButtonClassic,
    UilMinus,
    UilPlus,
  },
  props: {
    /**
     * Valeur de la quantité.
     */
    modelValue: {
      type: Number,
      default: null,
    },
    /**
     * Spécifie un ensemble de couleurs pour les boutons "+" et "-".
     */
    white: {
      type: Boolean,
      default: false,
    },
    /**
     * Ajoute une ombre dans l'input (pour un hover sur un parent).
     */
    shadow: {
      type: Boolean,
      default: false,
    },
    /**
     * État de la valeur dans l'input.
     */
    status: {
      type: String,
      default: "default",
      validator: (value) => ["default", "valid", "error", "warning"].includes(value),
    },
    /**
     * Désactive l'input.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Valeur minimale.
     */
    min: {
      type: Number,
      default: 0,
    },
    /**
     * Valeur maximale.
     */
    max: {
      type: Number,
      default: 9999,
    },
    /**
     * Incrément au clic sur les boutons.
     */
    step: {
      type: Number,
      default: 1,
    },
    /**
     * Cache les boutons "+" et "-".
     */
    hideButtons: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché au changement de la quantité.
     * @param {Number} - Nouvelle quantité.
     */
    "update:modelValue",
  ],
  watch: {
    modelValue: {
      handler() {
        if (this.modelValue === null || this.modelValue === undefined) {
          this.$emit("update:modelValue", this.min || 1);
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Change la valeur de la quantité.
     * @param {Number} value Valeur à afficher.
     * @param {Boolean} validate Si true, valide la valeur tout de suite après son changement (si pas de `@blur`).
     */
    changeValue(value, validate) {
      if (!this.disabled && typeof value === "number" && !Number.isNaN(value)) {
        this.$emit("update:modelValue", value);

        if (validate) {
          this.$nextTick(() => {
            this.validateValue(false);
          });
        }
      }
    },
    /**
     * Vérifie que la valeur de l'input est cohérente.
     * @param {Boolean} toast Si true, affiche un toast.
     */
    validateValue(toast = true) {
      if (this.min > this.modelValue || (!this.modelValue && this.modelValue !== 0)) {
        this.$emit("update:modelValue", this.min);
        if (toast) {
          this.$toast.warning({
            title: this.$t("toast.quantite-minimale-x", { min: this.min }),
            content: this.$t("toast.quantite-changee"),
          });
        }
      } else if (this.max < this.modelValue) {
        this.$emit("update:modelValue", this.max);
        if (toast) {
          this.$toast.warning({
            title: this.$t("toast.quantite-maximale-x", { max: this.max }),
            content: this.$t("toast.quantite-changee"),
          });
        }
      }
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/forms/input_quantity.scss";
</style>
